/**
 * Edge Texture Debug Widget
 *
 * Displays the edge texture data used for per-edge distance calculation.
 * Shows edge coordinates and a visual representation of the skeleton edges.
 */

import * as THREE from 'three';
import { DebugWidget, createWidgetContainer } from '../../../utils/DebugOverlay';

export interface EdgeTextureInfo {
  texture: THREE.DataTexture;
  edgeCount: number;
  maxDistance: number;
  floorHeight: number;
  heightTolerance: number;
}

export class SDFDebugWidget implements DebugWidget {
  static readonly WIDGET_NAME = 'sdf-debug';

  name = SDFDebugWidget.WIDGET_NAME;
  container: HTMLElement;

  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private maxDisplayWidth = 376; // 400px container - 24px padding
  private edgeInfo: EdgeTextureInfo | null = null;
  private infoDisplay: HTMLElement;

  constructor() {
    this.container = createWidgetContainer('📊 Edge Texture Debug');

    // Create canvas for visualization
    this.canvas = document.createElement('canvas');
    this.canvas.style.cssText = `
      width: 100%;
      max-width: 100%;
      height: auto;
      border: 2px solid #4a90d9;
      border-radius: 4px;
      background: rgba(0, 0, 0, 0.3);
      display: block;
      object-fit: contain;
    `;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get 2d context for SDF canvas');
    this.ctx = ctx;
    this.container.appendChild(this.canvas);

    // Create info display
    this.infoDisplay = document.createElement('div');
    this.infoDisplay.style.cssText = `
      margin-top: 8px;
      padding: 8px;
      background: rgba(0, 0, 0, 0.3);
      border-radius: 4px;
      font-size: 11px;
      font-family: monospace;
      color: rgba(255, 255, 255, 0.9);
      max-height: 200px;
      overflow-y: auto;
    `;
    this.container.appendChild(this.infoDisplay);

    // Initial placeholder
    this.showPlaceholder('No edge texture available');
    this.updateInfoDisplay();
  }

  /**
   * Set the SDF result to visualize (legacy interface - accepts null)
   */
  setSDFResult(_result: null): void {
    // Legacy interface - just clear the display
    this.edgeInfo = null;
    this.showPlaceholder('No edge texture available');
    this.updateInfoDisplay();
  }

  /**
   * Set the edge texture info to visualize
   */
  setEdgeTextureInfo(info: EdgeTextureInfo | null): void {
    this.edgeInfo = info;
    if (info && info.edgeCount > 0) {
      this.renderEdgeVisualization(info);
    } else {
      this.showPlaceholder('No edge texture available');
    }
    this.updateInfoDisplay();
  }

  /**
   * Update called by debug overlay
   */
  update(): void {
    this.updateInfoDisplay();
  }

  /**
   * Render a visualization of the edge data
   */
  private renderEdgeVisualization(info: EdgeTextureInfo): void {
    const { texture, edgeCount } = info;
    const textureData = (texture.image as { data: Float32Array }).data;

    // Find bounds of all edges to scale visualization
    let minX = Infinity,
      minY = Infinity;
    let maxX = -Infinity,
      maxY = -Infinity;

    for (let i = 0; i < edgeCount; i++) {
      const startX = textureData[i * 4 + 0];
      const startY = textureData[i * 4 + 1];
      const endX = textureData[i * 4 + 2];
      const endY = textureData[i * 4 + 3];

      minX = Math.min(minX, startX, endX);
      minY = Math.min(minY, startY, endY);
      maxX = Math.max(maxX, startX, endX);
      maxY = Math.max(maxY, startY, endY);
    }

    // Add padding
    const rangeX = maxX - minX || 1;
    const rangeY = maxY - minY || 1;
    const padding = Math.max(rangeX, rangeY) * 0.1;
    minX -= padding;
    minY -= padding;
    maxX += padding;
    maxY += padding;

    // Set canvas size
    const canvasSize = 256;
    this.canvas.width = canvasSize;
    this.canvas.height = canvasSize;
    this.canvas.style.width = `${this.maxDisplayWidth}px`;
    this.canvas.style.height = `${this.maxDisplayWidth}px`;

    // Clear canvas
    this.ctx.fillStyle = 'rgba(20, 20, 40, 1)';
    this.ctx.fillRect(0, 0, canvasSize, canvasSize);

    // Transform function from world coords to canvas coords
    const worldToCanvas = (wx: number, wy: number): { x: number; y: number } => {
      const x = ((wx - minX) / (maxX - minX)) * canvasSize;
      const y = canvasSize - ((wy - minY) / (maxY - minY)) * canvasSize; // Flip Y
      return { x, y };
    };

    // Draw grid
    this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    this.ctx.lineWidth = 0.5;
    for (let i = 0; i <= 10; i++) {
      const x = (i / 10) * canvasSize;
      const y = (i / 10) * canvasSize;
      this.ctx.beginPath();
      this.ctx.moveTo(x, 0);
      this.ctx.lineTo(x, canvasSize);
      this.ctx.stroke();
      this.ctx.beginPath();
      this.ctx.moveTo(0, y);
      this.ctx.lineTo(canvasSize, y);
      this.ctx.stroke();
    }

    // Draw edges
    this.ctx.lineWidth = 2;
    for (let i = 0; i < edgeCount; i++) {
      const startX = textureData[i * 4 + 0];
      const startY = textureData[i * 4 + 1];
      const endX = textureData[i * 4 + 2];
      const endY = textureData[i * 4 + 3];

      const start = worldToCanvas(startX, startY);
      const end = worldToCanvas(endX, endY);

      // Color edges with rainbow based on index
      const hue = (i / edgeCount) * 360;
      this.ctx.strokeStyle = `hsl(${hue}, 80%, 60%)`;

      this.ctx.beginPath();
      this.ctx.moveTo(start.x, start.y);
      this.ctx.lineTo(end.x, end.y);
      this.ctx.stroke();

      // Draw start point (green)
      this.ctx.fillStyle = '#00ff00';
      this.ctx.beginPath();
      this.ctx.arc(start.x, start.y, 3, 0, Math.PI * 2);
      this.ctx.fill();

      // Draw end point (red)
      this.ctx.fillStyle = '#ff0000';
      this.ctx.beginPath();
      this.ctx.arc(end.x, end.y, 3, 0, Math.PI * 2);
      this.ctx.fill();
    }

    // Draw axis labels
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    this.ctx.font = '10px monospace';
    this.ctx.fillText(`X: ${minX.toFixed(1)} to ${maxX.toFixed(1)}`, 5, canvasSize - 5);
    this.ctx.fillText(`Y: ${minY.toFixed(1)} to ${maxY.toFixed(1)}`, 5, 12);
  }

  /**
   * Update the info display with current edge data
   */
  private updateInfoDisplay(): void {
    if (!this.edgeInfo || this.edgeInfo.edgeCount === 0) {
      this.infoDisplay.innerHTML = `
        <div style="color: rgba(255, 255, 255, 0.5);">No edge data</div>
      `;
      return;
    }

    const { texture, edgeCount, maxDistance, floorHeight, heightTolerance } = this.edgeInfo;
    const textureData = (texture.image as { data: Float32Array }).data;

    // Build edge list HTML
    let edgeListHtml = '';
    const maxEdgesToShow = Math.min(10, edgeCount);
    for (let i = 0; i < maxEdgesToShow; i++) {
      const startX = textureData[i * 4 + 0];
      const startY = textureData[i * 4 + 1];
      const endX = textureData[i * 4 + 2];
      const endY = textureData[i * 4 + 3];
      const length = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));

      edgeListHtml += `
        <div style="margin-top: 2px;">
          <span style="color: hsl(${(i / edgeCount) * 360}, 80%, 60%);">Edge ${i}:</span>
          (${startX.toFixed(1)}, ${startY.toFixed(1)}) → (${endX.toFixed(1)}, ${endY.toFixed(1)})
          <span style="color: #888;">[${length.toFixed(2)}m]</span>
        </div>
      `;
    }
    if (edgeCount > maxEdgesToShow) {
      edgeListHtml += `<div style="color: #888;">... and ${edgeCount - maxEdgesToShow} more edges</div>`;
    }

    this.infoDisplay.innerHTML = `
      <div><span style="color: #4a90d9;">Edge Count:</span> ${edgeCount}</div>
      <div><span style="color: #4a90d9;">Max Distance:</span> ${maxDistance.toFixed(2)}m</div>
      <div><span style="color: #4a90d9;">Floor Height:</span> ${floorHeight.toFixed(2)}m</div>
      <div><span style="color: #4a90d9;">Height Tolerance:</span> ±${heightTolerance.toFixed(2)}m</div>
      <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid rgba(255,255,255,0.1);">
        <span style="color: #00ff00;">●</span> Start point
        <span style="margin-left: 8px; color: #ff0000;">●</span> End point
      </div>
      <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid rgba(255,255,255,0.1);">
        <strong>Edge Coordinates:</strong>
        ${edgeListHtml}
      </div>
    `;
  }

  /**
   * Show placeholder when no texture available
   */
  private showPlaceholder(message: string): void {
    this.canvas.width = this.maxDisplayWidth;
    this.canvas.height = 200;

    this.canvas.style.removeProperty('width');
    this.canvas.style.height = 'auto';

    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    this.ctx.font = '14px monospace';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText(message, this.canvas.width / 2, this.canvas.height / 2);
  }

  destroy(): void {
    this.edgeInfo = null;
  }
}
